// Canonical Diff Vader council reviewer set. Mirrors `SKILL_MAP` in
// rewind-corp/engineering-plugins/plugins/diff-vader/skills/dark-council/scripts/run-council.py.
// Order matches that file. Two parallel arrays because the surfaces speak
// different conventions:
//   - calibration UI groups by short slug ('correctness') — the form
//     `meta.reviewer_status` uses on the orchestrator wire.
//   - suppressions store the full skill id ('pr-reviewer-correctness') —
//     the form findings carry. See normalizeReviewer() in
//     packages/api/src/lib/calibration-rollup.ts.
//
// The two are NOT universally related by the 'pr-reviewer-' prefix. Upstream
// `SKILL_MAP` maps the council name 'security-tenant-isolation' to the skill id
// 'pr-reviewer-security' — the reviewer's scope outgrew its original name, the
// SKILL was renamed and the council key was not. Every divergent pair must be
// listed in SLUG_ID_EXCEPTIONS below; test/council-reviewers.test.ts asserts the
// arrays are prefix-aligned *except* for exactly those pairs, so a new
// divergence fails CI rather than silently splitting a reviewer in two.
//
// Assuming the prefix relationship held cost ten weeks of blind calibration:
// runs (keyed by slug) and findings (keyed by id) landed in different
// per_reviewer buckets, so the security reviewer showed 0 findings against ~600
// runs in one bucket and ~10 findings against 0 runs in the other. Both drift
// metrics gate on MIN_SAMPLE, so neither could ever fire. See death-star#322.

// Prefix every diff-vader reviewer SKILL id carries. Exported so the prefix
// exists in exactly one place — normalizeReviewer strips it, and the alias map
// below derives stripped forms with it.
export const SKILL_ID_PREFIX = 'pr-reviewer-';

export const COUNCIL_REVIEWER_SLUGS = [
  'correctness',
  'security-tenant-isolation',
  'test-adequacy',
  'blast-radius',
  'performance',
  'backup-correctness',
  'loop-design-system',
  'supply-chain',
  'supportability',
  'iac',
  'revertibility',
  'migration-safety',
  'github-actions',
  'generated-client',
  'cost',
  'contract-consistency',
  'rails-config',
  'ui-copy',
] as const;

export const COUNCIL_REVIEWER_IDS = [
  'pr-reviewer-correctness',
  // NOT 'pr-reviewer-security-tenant-isolation' — that skill id does not exist
  // upstream. See SLUG_ID_EXCEPTIONS.
  'pr-reviewer-security',
  'pr-reviewer-test-adequacy',
  'pr-reviewer-blast-radius',
  'pr-reviewer-performance',
  'pr-reviewer-backup-correctness',
  'pr-reviewer-loop-design-system',
  'pr-reviewer-supply-chain',
  'pr-reviewer-supportability',
  'pr-reviewer-iac',
  'pr-reviewer-revertibility',
  'pr-reviewer-migration-safety',
  'pr-reviewer-github-actions',
  'pr-reviewer-generated-client',
  'pr-reviewer-cost',
  'pr-reviewer-contract-consistency',
  'pr-reviewer-rails-config',
  'pr-reviewer-ui-copy',
] as const;

export type CouncilReviewerSlug = typeof COUNCIL_REVIEWER_SLUGS[number];
export type CouncilReviewerId = typeof COUNCIL_REVIEWER_IDS[number];

// Pairs where the skill id is NOT 'pr-reviewer-' + slug. Keyed by slug so the
// test can look up the expected id directly. Anything absent here must be
// prefix-aligned.
//
// Typed against the const arrays on BOTH sides on purpose: a typo in either
// half ('pr-reviewer-securty') would silently reintroduce the bucket split this
// map exists to record, and the runtime tests can only compare what they are
// given. Partial<> because most reviewers have no exception.
export const SLUG_ID_EXCEPTIONS: Readonly<Partial<Record<CouncilReviewerSlug, CouncilReviewerId>>> = {
  'security-tenant-isolation': 'pr-reviewer-security',
};

// id -> slug, the direction normalizeReviewer() needs. Derived from the two
// arrays rather than hand-written so it cannot drift from them.
//
// The VALUE is a CouncilReviewerSlug, but the KEY stays a bare string: this is
// looked up with arbitrary wire input, and normalizeReviewer deliberately falls
// back to a prefix strip for any reviewer added upstream but not yet registered
// here. Typing the key as CouncilReviewerId would assert the lookup is total
// when it is knowingly partial, and force a cast at the only call site.
// `noUncheckedIndexedAccess` already yields `CouncilReviewerSlug | undefined`
// on access, which is what that fallback consumes.
// Null-prototype, because both maps below are indexed with arbitrary wire input
// and `Object.fromEntries` produces an ordinary object. `map['toString']` on one
// of those resolves up the prototype chain to a Function, which passes an
// `=== undefined` guard and would be returned from normalizeReviewer — declared
// to return a string. No reviewer is called `toString` or `__proto__`, so this
// is unreachable with real data; it is fixed at the definition rather than
// guarded at each call site so future consumers cannot reintroduce it.
function lookupTable(
  entries: Array<[string, CouncilReviewerSlug]>,
): Readonly<Record<string, CouncilReviewerSlug>> {
  return Object.assign(Object.create(null) as Record<string, CouncilReviewerSlug>, Object.fromEntries(entries));
}

export const COUNCIL_SLUG_BY_ID: Readonly<Record<string, CouncilReviewerSlug>> =
  lookupTable(
    COUNCIL_REVIEWER_IDS.map(
      (id, i): [string, CouncilReviewerSlug] => [id, COUNCIL_REVIEWER_SLUGS[i]!],
    ),
  );

// Every alias that resolves to a council slug. There are THREE wire forms for a
// reviewer, not two, and #322 only handled the first two:
//
//   meta.reviewer_status              -> council name     'security-tenant-isolation'
//   findings[].reviewer               -> full skill id    'pr-reviewer-security'
//   findings_anchor_status.by_reviewer -> STRIPPED id     'security'
//
// The runner strips the prefix itself on that third one, so it arrives as a bare
// name that looks like a slug but is not one. For prefix-aligned reviewers that
// is harmless — the stripped id and the slug are the same string. For a
// divergent pair it is a third distinct spelling, and a lookup keyed only by
// full id misses it, leaving the reviewer's anchor counters in their own bucket
// (death-star#330).
//
// Derived from SLUG_ID_EXCEPTIONS rather than hand-written, so the next
// divergent pair is covered by declaring it once.
export const COUNCIL_SLUG_BY_ALIAS: Readonly<Record<string, CouncilReviewerSlug>> = lookupTable([
  ...COUNCIL_REVIEWER_IDS.map(
    (id, i): [string, CouncilReviewerSlug] => [id, COUNCIL_REVIEWER_SLUGS[i]!],
  ),
  ...Object.entries(SLUG_ID_EXCEPTIONS).map(
    ([slug, id]): [string, CouncilReviewerSlug] => [
      id!.startsWith(SKILL_ID_PREFIX) ? id!.slice(SKILL_ID_PREFIX.length) : id!,
      slug as CouncilReviewerSlug,
    ],
  ),
]);

// Reviewers that have NO severity axis: their findings are binary, so `severity`
// is always null by design. NOT a data gap — see the "This reviewer has no
// severity axis" section in each upstream SKILL for the per-reviewer reasoning.
//
// Why the UI needs this at all. `severity` is required on reviewer output
// (engineering-plugins#181), and a reviewer with no axis satisfies that with an
// explicit null. Those nulls bucket to SEVERITY_UNSPECIFIED, where they look
// identical to a reviewer that HAS a scale and never fired above it. Rendering
// both as an empty distribution is how a suppressed reviewer hides in plain
// sight: it is exactly what made pr-reviewer-correctness's 0.0% substantive
// rate unreadable for three weeks (rewind-corp/diff-vader#223), and it took a
// DynamoDB replay to discover the field was never emitted at all. A reviewer
// listed here renders as "n/a — no severity axis" instead.
//
// Slug form, because the calibration UI groups by slug. MIRRORS
// SEVERITY_FREE_REVIEWERS in engineering-plugins
// plugins/diff-vader/skills/dark-council/scripts/run-council.py, which is keyed
// by skill id — the same two-conventions split COUNCIL_REVIEWER_SLUGS /
// COUNCIL_REVIEWER_IDS exists for, and the reason this is a slug list rather
// than a copy-paste of the upstream set. Change both together.
//
// Typed as CouncilReviewerSlug[] so a typo or a renamed reviewer fails the
// build here rather than silently declaring nothing — the failure mode this
// file's header documents for the slug/id split.
export const SEVERITY_FREE_REVIEWER_SLUGS: readonly CouncilReviewerSlug[] = [
  'generated-client',
  'revertibility',
  'loop-design-system',
] as const;

const SEVERITY_FREE_SET: ReadonlySet<string> = new Set(SEVERITY_FREE_REVIEWER_SLUGS);

/**
 * Does this reviewer have a severity axis at all?
 *
 * Takes either form — slug ('revertibility') or skill id
 * ('pr-reviewer-revertibility') — because callers on the API side hold ids from
 * finding rows while the portal holds slugs from the route param, and making
 * every caller normalize first is how one of them eventually forgets. Unknown
 * reviewers return true: a reviewer added upstream but not yet registered here
 * is far more likely to have a scale than not, and defaulting to "has an axis"
 * shows an empty distribution (visibly odd, prompts a look) rather than
 * asserting "n/a" (looks deliberate, hides the gap).
 */
export function hasSeverityAxis(reviewer: string): boolean {
  // ALIAS, not BY_ID: the stripped skill id is a third wire form (see
  // COUNCIL_SLUG_BY_ALIAS). No reviewer is both severity-free and divergent
  // today, so this is not a live defect — but it is the same lookup that opened
  // a second bucket in death-star#330, and there is no reason to leave the two
  // consumers of this mapping disagreeing about what counts as a reviewer name.
  const slug = COUNCIL_SLUG_BY_ALIAS[reviewer] ?? reviewer;
  return !SEVERITY_FREE_SET.has(slug);
}
